import React from 'react';
import { ModalHeader, ModalBody, ModalFooter, Button } from 'reactstrap';
import DialogModal from '~/components/DialogModal';
import { idGenerator } from '~/lib/helper/IdHelper';

type ErrorModalProps = {
    title: string;
    children: React.ReactNode;
    onDismiss: () => void;
};

const nextId = idGenerator('error-modal-');

export default class ErrorModal extends React.Component<ErrorModalProps> {
    private modalRef = React.createRef<HTMLDivElement>();
    private closeButtonRef = React.createRef<HTMLButtonElement>();
    private titleId = nextId();
    private bodyId = nextId();

    // Scoped to this modal's DOM subtree: only dismisses when the keypress originated inside
    // this modal, so Esc/Enter cannot leak through to underlying dialogs (e.g. DepositModal,
    // EnableIpv6Button) that intentionally stay open after failure. stopImmediatePropagation
    // defends against sibling reactstrap <Modal> handlers also picking up the same event.
    //
    // Reactstrap's <Modal> does have a built-in Escape handler (onKeyUp on the modal div) that
    // would handle Esc dismissal, but: (a) it only covers Escape, not Enter, (b) it relies on
    // focus being inside the modal which JSDOM's portal-aware event system does not honour
    // reliably for unit tests (autoFocus targets the outer wrapper but Esc keyup events don't
    // reach reactstrap's handler in our test setup — confirmed by activeElement diagnostic).
    // Keeping our own handler ensures consistent behaviour in JSDOM and production browsers.
    handleKeyDown = (e: KeyboardEvent) => {
        if (e.key !== 'Enter' && e.key !== 'Escape') return;
        const modalRoot = this.modalRef.current;
        if (!modalRoot?.contains(e.target as Node)) return;
        e.preventDefault();
        e.stopImmediatePropagation();
        this.props.onDismiss();
    };

    componentDidMount() {
        document.addEventListener('keydown', this.handleKeyDown, true);
    }

    // Focus the Close button after reactstrap completes its own focus management
    // (autoFocus on the modal wrapper). Running this in componentDidMount races and
    // loses to reactstrap; onOpened fires post-transition and reliably wins.
    handleOpened = () => {
        this.closeButtonRef.current?.focus();
    };

    componentWillUnmount() {
        document.removeEventListener('keydown', this.handleKeyDown, true);
    }

    render() {
        const { title, children, onDismiss } = this.props;
        return (
            <DialogModal
                isOpen={true}
                toggle={onDismiss}
                innerRef={this.modalRef}
                role="alertdialog"
                labelledBy={this.titleId}
                aria-describedby={this.bodyId}
                onOpened={this.handleOpened}
            >
                <ModalHeader>
                    {/* id on the title span, not the ModalHeader — see PanelSite.md "Modals". */}
                    <span id={this.titleId}>{title}</span>
                </ModalHeader>
                <ModalBody id={this.bodyId}>{children}</ModalBody>
                <ModalFooter>
                    <Button color="primary" onClick={onDismiss} innerRef={this.closeButtonRef}>
                        Close
                    </Button>
                </ModalFooter>
            </DialogModal>
        );
    }
}
